import Phaser from 'phaser';
import { ASTEROID_VARIANTS } from '../game/config/asteroidVariants';
import {
  ASTEROID_RENDER_KEY,
  ASTEROID_RENDER_REVISION,
  drawAsteroidGraphics,
} from '../game/rendering/AsteroidGraphicsRenderer';

const GALLERY_WIDTH = 1120;
const CARD_WIDTH = 280;
const CARD_HEIGHT = 250;
const HEADER_HEIGHT = 80;
const GALLERY_HEIGHT = HEADER_HEIGHT + ASTEROID_VARIANTS.length * CARD_HEIGHT;
const DISPLAY_SCALE = 4;

type GalleryMode = 'base' | 'rotated' | 'mirrored' | 'damaged-hitbox';

const MODES: readonly GalleryMode[] = ['base', 'rotated', 'mirrored', 'damaged-hitbox'];

declare global {
  interface Window {
    __ASTEROID_GALLERY_DEBUG__?: {
      renderKey: string;
      renderRevision: number;
      variantIds: string[];
      renderedCount: number;
      hasProceduralVariants: false;
      modes: readonly GalleryMode[];
    };
  }
}

export class AsteroidGalleryApp {
  private game?: Phaser.Game;

  public constructor(private readonly root: HTMLElement) {}

  public start(): void {
    document.body.classList.add('asteroid-gallery-mode');
    this.root.innerHTML =
      '<main id="asteroid-gallery-page"><div id="asteroid-gallery-canvas"></div></main>';
    window.__ASTEROID_GALLERY_DEBUG__ = {
      renderKey: ASTEROID_RENDER_KEY,
      renderRevision: ASTEROID_RENDER_REVISION,
      variantIds: ASTEROID_VARIANTS.map((variant) => variant.id),
      renderedCount: ASTEROID_VARIANTS.length * MODES.length,
      hasProceduralVariants: false,
      modes: MODES,
    };
    this.game = new Phaser.Game({
      type: Phaser.CANVAS,
      parent: 'asteroid-gallery-canvas',
      width: GALLERY_WIDTH,
      height: GALLERY_HEIGHT,
      backgroundColor: '#050714',
      pixelArt: true,
      antialias: false,
      roundPixels: true,
      input: { mouse: { preventDefaultWheel: false } },
      render: { antialias: false, pixelArt: true, roundPixels: true },
      scene: [AsteroidGalleryScene],
    });
  }

  public destroy(): void {
    this.game?.destroy(true);
    document.body.classList.remove('asteroid-gallery-mode');
    delete window.__ASTEROID_GALLERY_DEBUG__;
  }
}

class AsteroidGalleryScene extends Phaser.Scene {
  public constructor() {
    super('asteroid-gallery');
  }

  public create(): void {
    this.cameras.main.setBackgroundColor('#050714');
    this.add
      .text(24, 18, `ASTEROID RENDER PIPELINE · ${ASTEROID_RENDER_KEY}`, {
        color: '#f5f7ff',
        fontFamily: 'Courier New, monospace',
        fontSize: '24px',
      })
      .setResolution(2);
    this.add
      .text(24, 49, 'actual Phaser Graphics · 4× · cyan = collision ellipse', {
        color: '#55d9e8',
        fontFamily: 'Courier New, monospace',
        fontSize: '15px',
      })
      .setResolution(2);

    ASTEROID_VARIANTS.forEach((variant, row) => {
      MODES.forEach((mode, column) => {
        const left = column * CARD_WIDTH;
        const top = HEADER_HEIGHT + row * CARD_HEIGHT;
        this.add.rectangle(
          left + CARD_WIDTH / 2,
          top + CARD_HEIGHT / 2,
          CARD_WIDTH - 12,
          CARD_HEIGHT - 12,
          0x11162b,
        );
        const centerX = left + CARD_WIDTH / 2;
        const centerY = top + 108;
        const asteroid = this.add.graphics();
        drawAsteroidGraphics(asteroid, variant);
        asteroid.setPosition(centerX, centerY).setScale(DISPLAY_SCALE);
        if (mode === 'rotated') asteroid.setRotation(Math.PI / 4);
        if (mode === 'mirrored') asteroid.setScale(-DISPLAY_SCALE, DISPLAY_SCALE);
        if (mode === 'damaged-hitbox') {
          asteroid.setAlpha(0.62);
          this.add
            .ellipse(
              centerX + variant.hitbox.offsetX * DISPLAY_SCALE,
              centerY + variant.hitbox.offsetY * DISPLAY_SCALE,
              variant.hitbox.width * DISPLAY_SCALE,
              variant.hitbox.height * DISPLAY_SCALE,
            )
            .setStrokeStyle(2, 0x55d9e8, 1);
        }
        this.add
          .text(
            centerX,
            top + 188,
            `${ASTEROID_RENDER_KEY}\n${variant.id}\n${variant.sizeClass} · ${mode}`,
            {
              align: 'center',
              color: '#f5f7ff',
              fontFamily: 'Courier New, monospace',
              fontSize: '12px',
            },
          )
          .setOrigin(0.5, 0)
          .setResolution(2);
      });
    });
  }
}
